import React, { useEffect, useState } from "react";
import {
	Button,
	Col,
	DatePicker,
	Flex,
	notification,
	Table,
	Typography,
	Row,
	Form,
	InputNumber,
	Statistic,
	Modal,
	Tag,
} from "antd";
import Layout from "../../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../../utils/CustomComponents";
import { api } from "../../utils/Api";
import axios from "axios";
import dayjs from "dayjs";
import { useRef } from "react";
import ReceptionDetailsByMaterial from "./ReceptionDetailsByMaterial";
import {
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
	BarChart,
	Bar,
	Cell,
} from "recharts";
import { RetweetOutlined } from "@ant-design/icons";
import InventoryReceptionComment from "./InventoryReceptionComment";
const { Text } = Typography;

const InventoryDashboard = () => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [date, setDate] = useState(dayjs());
	const [list, setList] = useState([]);
	const [plant, setPlant] = useState(null);
	const [plantList, setPlantList] = useState([]);
	const [currentId, setCurrentId] = useState("");
	const [modalDetailsOpen, setModalDetailsOpen] = useState(false);
	const [dataGraph, setDataGraph] = useState([]);
	const [approvalHistory, setApprovalHistory] = useState([]);
	const [refresh, setRefresh] = useState([]);
	const [approvalStatus, setApprovalStatus] = useState("");
	const [modalCommentOpen, setModalCommentOpen] = useState(false);
	const [approved, setApproved] = useState(false);
	const materialId = useRef(null);

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const [form] = Form.useForm();
	const [editingKey, setEditingKey] = useState("");
	const isEditing = (record) => record.key === editingKey;

	const edit = (record) => {
		form.setFieldsValue({ ...record });
		setEditingKey(record.key);
	};

	const cancel = () => {
		setEditingKey("");
	};

	const save = async (key) => {
		try {
			const row = await form.validateFields();
			const newData = [...list];
			const index = newData.findIndex((item) => key === item.key);

			if (index > -1) {
				const item = newData[index];

				const unitEfficiency = row.physical - item.books;
				const unitCostPhysical = row.physical * row.unitCost;
				const unitCostBooks = item.books * row.unitCost;
				const unitCostEfficiency = unitCostPhysical - unitCostBooks;
				const efficiencyCogs =
					item.cogs > 0 ? (unitEfficiency / item.cogs) * 100 : 0;

				const newBody = {
					...row,
					unitCostPhysical: unitCostPhysical,
					unitCostBooks: unitCostBooks,
					unitEfficiency: unitEfficiency,
					unitCostEfficiency: unitCostEfficiency,
					efficiencyCogs: efficiencyCogs,
				};

				const isUpdated = await updateMaterialRow(item._id, newBody);
				if (isUpdated) {
					newData.splice(index, 1, { ...item, ...newBody });
					setList(newData);
					setEditingKey("");
				}
			}
		} catch (errInfo) {
			console.log("Validate Failed:", errInfo);
		}
	};

	const updateMaterialRow = async (id, fields) => {
		setLoadingTable(true);

		const url = api + `/receptioninventory/update/${currentId}/${id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.patch(url, fields);
			if (data.success) {
				setLoadingTable(false);
				return true;
			} else {
				openNotification("error", "Error", data.message);
				setLoadingTable(false);
				return false;
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return false;
		}
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Actions",
			dataIndex: "actions",
			width: 180,
			render: (_, record) =>
				editingKey === record.key ? (
					<Row gutter={[8, 8]}>
						<Col>
							<Button type="primary" onClick={() => save(record.key)}>
								Save
							</Button>
						</Col>
						<Col>
							<Button type="primary" danger onClick={cancel}>
								Cancel
							</Button>
						</Col>
					</Row>
				) : (
					<Row gutter={[8, 8]}>
						<Col>
							<Button
								type="primary"
								onClick={() => edit(record)}
								disabled={approvalStatus === "Closed"}
							>
								Edit
							</Button>
						</Col>
						<Col>
							<Button
								type="dashed"
								onClick={() => {
									materialId.current = record.cogId._id;
									setModalDetailsOpen(true);
								}}
							>
								Details
							</Button>
						</Col>
					</Row>
				),
		},
		{
			title: "SKU",
			dataIndex: "sku",
			key: "sku",
			width: 150,
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: "Material",
			dataIndex: "material",
			key: "material",
			width: 150,
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: "U/M",
			dataIndex: "um",
			key: "um",
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: "B.I. ADJ",
			dataIndex: "prevPhysical",
			key: "prevPhysical",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Inventory Purchases",
			dataIndex: "inventoryPurchases",
			key: "inventoryPurchases",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Total Available Inv. in Hand",
			dataIndex: "totalAvailable",
			key: "totalAvailable",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "COGS",
			dataIndex: "cogs",
			key: "cogs",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Drone Inventory",
			dataIndex: "droneInventory",
			key: "droneInventory",
			editable: true,
		},
		{
			title: "Books E.I.",
			dataIndex: "books",
			key: "books",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Physical E.I.",
			dataIndex: "physical",
			key: "physical",
			editable: true,
		},
		{
			title: "Batch E.I.",
			dataIndex: "batch",
			key: "batch",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Unit Cost",
			dataIndex: "unitCost",
			key: "unitCost",
			editable: true,
		},
		{
			title: "Unit Efficiency",
			dataIndex: "unitEfficiency",
			key: "unitEfficiency",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "% Efficiency vs COGS",
			dataIndex: "efficiencyCogs",
			key: "efficiencyCogs",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					suffix={"%"}
				/>
			),
		},
		{
			title: "Unit Cost Physical",
			dataIndex: "unitCostPhysical",
			key: "unitCostPhysical",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Unit Cost Books",
			dataIndex: "unitCostBooks",
			key: "unitCostBooks",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
		{
			title: "Unit Cost Efficiency",
			dataIndex: "unitCostEfficiency",
			key: "unitCostEfficiency",
			render: (value) => (
				<Statistic
					value={value}
					precision={2}
					valueStyle={{
						fontSize: 14,
					}}
					prefix={"$"}
				/>
			),
		},
	];

	/**
	 ** Options header (columns)
	 */
	const columnsApproval = [
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (value) => {
				let color = "default";
				switch (value) {
					case "Pending":
						color = "yellow";
						break;
					case "Approved":
						color = "green";
						break;
					case "Rejected":
						color = "red";
						break;
					case "Reopen":
						color = "purple";
						break;
					default:
						break;
				}
				return <Tag color={color}>{value}</Tag>;
			},
		},
		// {
		// 	title: "Name",
		// 	dataIndex: "approvedRejectedBy",
		// 	key: "approvedRejectedBy",
		// 	render: (value) => <Text>{value}</Text>,
		// },
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: "Datetime",
			dataIndex: "date",
			key: "date",
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: "Comments",
			dataIndex: "comments",
			key: "comments",
			render: (value) => <Text>{value}</Text>,
		},
	];

	const EditableCell = ({
		editing,
		dataIndex,
		title,
		inputType,
		record,
		index,
		children,
		...restProps
	}) => {
		let value = "";
		if (
			dataIndex === "droneInventory" ||
			dataIndex === "physical" ||
			dataIndex === "unitCost"
		) {
			value = record[dataIndex];
		}
		return (
			<td {...restProps}>
				{editing ? (
					<Form.Item
						name={dataIndex}
						style={{ margin: 0 }}
						rules={[{ required: true, message: `Please Input ${title}!` }]}
					>
						<InputNumber
							size="large"
							min={0}
							formatter={(value) =>
								`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
							}
							parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
						/>
					</Form.Item>
				) : dataIndex === "droneInventory" ||
				  dataIndex === "physical" ||
				  dataIndex === "unitCost" ? (
					<Statistic
						value={value}
						precision={2}
						valueStyle={{
							fontSize: 14,
						}}
						prefix={"$"}
					/>
				) : (
					children
				)}
			</td>
		);
	};

	const mergedColumns = columns.map((col) => {
		if (!col.editable) return col;

		return {
			...col,
			onCell: (record) => ({
				record,
				inputType: "text",
				dataIndex: col.dataIndex,
				title: col.title,
				// ✅ Only mark as editing if it's the row being edited
				editing: isEditing(record),
			}),
		};
	});

	const summary = (pageData) => {
		let unitCostPhysicalTotal = 0;
		let unitCostBooksTotal = 0;
		let unitCostEfficiencyTotal = 0;

		pageData.forEach(
			({ unitCostPhysical, unitCostBooks, unitCostEfficiency }) => {
				unitCostPhysicalTotal += unitCostPhysical;
				unitCostBooksTotal += unitCostBooks;
				unitCostEfficiencyTotal += unitCostEfficiency;
			},
		);

		return (
			<>
				<Table.Summary.Row>
					<Table.Summary.Cell colSpan={14} />
					<Table.Summary.Cell>
						<Text style={{ fontSize: 14 }} strong>
							{"Total"}
						</Text>
					</Table.Summary.Cell>
					<Table.Summary.Cell>
						<Statistic
							title=""
							value={unitCostPhysicalTotal}
							style={{ fontWeight: "bold" }}
							precision={2}
							valueStyle={{ fontSize: 14 }}
							prefix={"$"}
						/>
					</Table.Summary.Cell>
					<Table.Summary.Cell>
						<Statistic
							title=""
							value={unitCostBooksTotal}
							style={{ fontWeight: "bold" }}
							precision={2}
							valueStyle={{ fontSize: 14 }}
							prefix={"$"}
						/>
					</Table.Summary.Cell>
					<Table.Summary.Cell>
						<Statistic
							title=""
							value={unitCostEfficiencyTotal}
							style={{ fontWeight: "bold" }}
							precision={2}
							valueStyle={{ fontSize: 14 }}
							prefix={"$"}
						/>
					</Table.Summary.Cell>
				</Table.Summary.Row>
			</>
		);
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<Form form={form} component={false}>
					<Table
						components={{
							body: {
								cell: EditableCell,
							},
						}}
						bordered
						dataSource={list}
						columns={mergedColumns}
						rowClassName="editable-row"
						pagination={{
							onChange: cancel,
						}}
						loading={loadingTable}
						summary={summary}
					/>
				</Form>
			</Flex>
		);
	};

	useEffect(() => {
		if (date && plant !== null) {
			getReceptionInventory(date, plant);
		}
	}, [date, plant, refresh]);

	useEffect(() => {
		getPlants();
		// createInventory();
	}, []);

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item) => {
					list.push({
						value: item._id,
						label: item.name,
					});
				});
				setPlantList(list);
				if (localStorage.getItem("role") === "Batch") {
					setPlant(localStorage.getItem("plant"));
				} else {
					setPlant(list.find((p) => p.label === "Mission").value);
				}
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getReceptionInventory = async (date, plant) => {
		try {
			setLoading(true);
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const year = dayjs(date).year();
			const month = dayjs(date).month() + 1;

			const url =
				api +
				`/receptioninventory/get/bydate?year=${year}&month=${month}&plant=${plant}`;
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				let listGraph = [];
				data.payload.items.map((item) => {
					list.push({
						_id: item._id,
						index: item._id,
						key: item._id,
						cogId: item.sku,
						sku: item.sku.material,
						material: item.sku.material,
						um: item.sku.inventoryUnitOfMeasurement,
						prevPhysical: item.prevPhysical,
						inventoryPurchases: item.inventoryPurchases,
						totalAvailable: item.totalAvailable,
						cogs: item.cogs,
						droneInventory: item.droneInventory,
						books: item.books,
						physical: item.physical,
						batch: item.batch,
						unitCost: item.unitCost,
						unitEfficiency: item.unitEfficiency,
						efficiencyCogs: item.efficiencyCogs,
						unitCostPhysical: item.unitCostPhysical,
						unitCostBooks: item.unitCostBooks,
						unitCostEfficiency: item.unitCostEfficiency,
						materialQuantity: item.materialQuantity,
					});

					listGraph.push({
						material: item.sku.material,
						quantity: item.materialQuantity,
					});
				});
				let approvalList = [];
				data.payload.approvalHistory.map((item, index) => {
					approvalList.push({
						key: index,
						status: item.status,
						name: item.sendItBy.name,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
						}).format(new Date(item.createdAt)),
						comments: item.comments,
					});
				});
				setApprovalHistory(approvalList);

				setApprovalStatus(data.payload.approvalStatus);
				setList(list);
				setDataGraph(listGraph);
				setCurrentId(data.payload._id);
			} else {
				setList([]);
				setApprovalHistory([]);
				setDataGraph([]);
				setApprovalStatus("");
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			setList([]);
			openNotification(
				"error",
				"Error",
				"Something went wrong while obtaining reception inventory",
			);
			console.error(err.message);
			return null;
		}
	};

	// const createInventory = async () => {
	// 	const token = localStorage.getItem("token");
	// 	axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

	// 	try {
	// 		const url = api + `/receptioninventory/create`;
	// 		const { data } = await axios.post(url);
	// 		if (data.success) {
	// 			openNotification("success", "Success", data.message);
	// 		} else {
	// 			openNotification("error", "Error", data.message);
	// 		}
	// 	} catch (err) {
	// 		openNotification(
	// 			"error",
	// 			"Error",
	// 			"Something went wrong while creating reception"
	// 		);
	// 		console.error(err.message);
	// 		return null;
	// 	}
	// };

	const onChangeDate = (date) => {
		setDate(date);
	};

	const onChangePlant = (value) => {
		setPlant(value);
	};

	const RenderFilters = () => {
		return (
			<Row gutter={[16, 16]}>
				<Col>
					<DatePicker
						onChange={onChangeDate}
						picker="month"
						format={"MM/YYYY"}
						allowClear={false}
						value={date}
						size="large"
					/>
				</Col>
				<Col>
					<CustomSearch
						loading={loading}
						allowClearPicker={false}
						labelPicker={"Plant"}
						optionsPicker={plantList}
						pickerWidth={"300px"}
						pickerValue={plant}
						onChangePicker={onChangePlant}
					/>
				</Col>
				<Col>
					<Button
						icon={<RetweetOutlined />}
						size="large"
						type="primary"
						onClick={() => setRefresh((prev) => !prev)}
					/>
				</Col>
			</Row>
		);
	};

	const ModalViewData = () => {
		return (
			<Modal
				title={``}
				open={modalDetailsOpen}
				onCancel={() => {
					setModalDetailsOpen(false);
					materialId.current = null;
				}}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
				centered
			>
				<ReceptionDetailsByMaterial
					plant={plant}
					date={date}
					materialId={materialId.current}
				/>
			</Modal>
		);
	};

	const ModalComment = () => {
		return (
			<Modal
				title={``}
				open={modalCommentOpen}
				onCancel={() => {
					setModalCommentOpen(false);
				}}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
				centered
			>
				<InventoryReceptionComment
					onComplete={
						approvalStatus === "Supervisor"
							? sendForApproval
							: sendForApprovalReject
					}
				/>
			</Modal>
		);
	};

	const RenderTableApproval = () => {
		return (
			<Flex gap={"middle"} vertical>
				<CustomTable
					columns={columnsApproval}
					data={approvalHistory}
					isScroll={false}
					loading={loadingTable}
				/>
			</Flex>
		);
	};

	const RenderGraph = () => {
		return (
			<ResponsiveContainer width="100%" height={600}>
				<BarChart
					data={dataGraph}
					margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
				>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis dataKey="material" />
					<YAxis />
					<Tooltip
						formatter={(value) => (
							<Statistic
								value={value}
								precision={2}
								valueStyle={{
									fontSize: 14,
								}}
							/>
						)}
					/>

					<Bar dataKey="quantity">
						{dataGraph.map((entry, index) => (
							<Cell
								key={`cell-${index}`}
								fill={index % 2 === 0 ? "#ac2727ff" : "#15155fff"} // red for even, blue for odd
							/>
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		);
	};

	const getReceptionInventoryHistory = async () => {
		try {
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const url = api + `/receptioninventory/get/history/${currentId}`;
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						ket: index,
						status: item.status,
						name: item.sendItBy.name,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
						}).format(new Date(item.createdAt)),
						comments: item.comments,
					});
				});
				setApprovalHistory(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification(
				"error",
				"Error",
				"Something went wrong while obtaining reception inventory history",
			);
			console.error(err.message);
			return null;
		}
	};

	const sendForApproval = async (comments) => {
		try {
			setModalCommentOpen(false);
			setLoadingTable(true);
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const url = api + `/receptioninventory/send/approval/${currentId}`;
			const { data } = await axios.post(url, { comments });
			if (data.success) {
				openNotification("success", "Success", data.message);
				setApprovalStatus("Director");
				await getReceptionInventoryHistory();
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			setLoadingTable(false);
			openNotification(
				"error",
				"Error",
				"Something went wrong while sending approval",
			);
			console.error(err.message);
			return null;
		}
	};

	const sendForApprovalAction = async () => {
		setModalCommentOpen(true);
		// await sendForApproval();
	};

	const sendForApprovalReject = async (comments) => {
		try {
			setModalCommentOpen(false);
			setLoadingTable(true);
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			let status = "";

			if (
				(localStorage.getItem("email") === "jp@57concrete.com" ||
					localStorage.getItem("email") === "eliud@gmail.com" ||
					localStorage.getItem("email") === "bpa@57concrete.com") &&
				approvalStatus === "Director"
			) {
				if (approved) {
					status = "Finance";
				} else {
					status = "Supervisor";
				}
			} else if (
				(localStorage.getItem("email") === "mv@57concrete.com" ||
					localStorage.getItem("email") === "eliud@gmail.com" ||
					localStorage.getItem("email") === "bpa@57concrete.com") &&
				approvalStatus === "Finance"
			) {
				if (approved) {
					status = "CEO";
				} else {
					status = "Director";
				}
			} else if (
				(localStorage.getItem("email") === "ec@57concrete.com" ||
					localStorage.getItem("email") === "pc@57concrete.com" ||
					localStorage.getItem("email") === "jc@57concrete.com" ||
					localStorage.getItem("email") === "gg@57xgroup.com" ||
					localStorage.getItem("email") === "eliud@gmail.com" ||
					localStorage.getItem("email") === "bpa@57concrete.com") &&
				approvalStatus === "CEO"
			) {
				if (approved) {
					status = "Closed";
				} else {
					status = "Finance";
				}
			}

			const body = {
				approved,
				approvalStatus: status,
				comments: comments,
				items: status === "Closed" ? list : [],
			};

			const url =
				api + `/receptioninventory/send/approvalrejected/${currentId}`;
			const { data } = await axios.post(url, body);
			if (data.success) {
				openNotification("success", "Success", data.message);
				setApprovalStatus(status);
				await getReceptionInventoryHistory();
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			setLoadingTable(false);
			openNotification(
				"error",
				"Error",
				"Something went wrong while sending approval",
			);
			console.error(err.message);
			return null;
		}
	};

	const onNextApproveRejectAction = async (approved) => {
		setApproved(approved);
		setModalCommentOpen(true);
		// await sendForApprovalReject(approved);
	};

	const reopenInventory = async () => {
		try {
			setLoadingTable(true);
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const url = api + `/receptioninventory/reopen/${currentId}`;
			const { data } = await axios.post(url);
			if (data.success) {
				setRefresh((prev) => !prev);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			setLoadingTable(false);
			openNotification(
				"error",
				"Error",
				"Something went wrong while reopening",
			);
			console.error(err.message);
			return null;
		}
	};

	return (
		<Layout title="Inventory Dashboard" description="Dashboard" type="large">
			<Flex vertical gap={"large"}>
				<RenderFilters />
				{loading ? (
					<CustomSpinner />
				) : (
					<Row gutter={[16, 16]}>
						<Col span={12}>
							<RenderGraph />
						</Col>
						<Col span={12}>
							<RenderTableApproval />
						</Col>
						<Col span={24}>
							<Row gutter={16} justify={"end"}>
								{((approvalStatus === "Director" &&
									(localStorage.getItem("email") === "jp@57concrete.com" ||
										localStorage.getItem("email") === "eliud@gmail.com" ||
										localStorage.getItem("email") === "bpa@57concrete.com")) ||
									(approvalStatus === "Finance" &&
										(localStorage.getItem("email") === "mv@57concrete.com" ||
											localStorage.getItem("email") === "eliud@gmail.com" ||
											localStorage.getItem("email") ===
												"bpa@57concrete.com")) ||
									(approvalStatus === "CEO" &&
										(localStorage.getItem("email") === "ec@57concrete.com" ||
											localStorage.getItem("email") === "pc@57concrete.com" ||
											localStorage.getItem("email") === "jc@57concrete.com" ||
											localStorage.getItem("email") === "gg@57xgroup.com" ||
											localStorage.getItem("email") === "eliud@gmail.com" ||
											localStorage.getItem("email") ===
												"bpa@57concrete.com"))) && (
									<Col>
										<Button
											size="large"
											danger
											type="primary"
											onClick={() => onNextApproveRejectAction(false)}
											loading={loadingTable}
										>
											Reject
										</Button>
									</Col>
								)}
								{((approvalStatus === "Director" &&
									(localStorage.getItem("email") === "jp@57concrete.com" ||
										localStorage.getItem("email") === "eliud@gmail.com" ||
										localStorage.getItem("email") === "bpa@57concrete.com")) ||
									(approvalStatus === "Finance" &&
										(localStorage.getItem("email") === "mv@57concrete.com" ||
											localStorage.getItem("email") === "eliud@gmail.com" ||
											localStorage.getItem("email") ===
												"bpa@57concrete.com")) ||
									(approvalStatus === "CEO" &&
										(localStorage.getItem("email") === "ec@57concrete.com" ||
											localStorage.getItem("email") === "pc@57concrete.com" ||
											localStorage.getItem("email") === "jc@57concrete.com" ||
											localStorage.getItem("email") === "gg@57xgroup.com" ||
											localStorage.getItem("email") === "eliud@gmail.com" ||
											localStorage.getItem("email") ===
												"bpa@57concrete.com"))) && (
									<Col>
										<Button
											size="large"
											type="primary"
											onClick={() => onNextApproveRejectAction(true)}
											loading={loadingTable}
										>
											Approve
										</Button>
									</Col>
								)}
								{approvalStatus === "Supervisor" &&
									(localStorage.getItem("email") === "eliud@gmail.com" ||
										localStorage.getItem("email") === "bpa@57concrete.com" ||
										localStorage.getItem("email") === "aca@57concrete.com" ||
										localStorage.getItem("email") === "ji@57concrete.com") && (
										<Col>
											<Button
												size="large"
												type="primary"
												onClick={sendForApprovalAction}
												loading={loadingTable}
											>
												Send for approval
											</Button>
										</Col>
									)}
								{approvalStatus === "Closed" &&
									(localStorage.getItem("email") === "eliud@gmail.com" ||
										localStorage.getItem("email") === "bpa@57concrete.com" ||
										localStorage.getItem("email") === "pc@57concrete.com" ||
										localStorage.getItem("email") === "jc@57concrete.com" ||
										localStorage.getItem("email") === "gg@57xgroup.com" ||
										localStorage.getItem("email") === "ec@57concrete.com") && (
										<Col>
											<Button
												size="large"
												type="primary"
												onClick={reopenInventory}
												loading={loadingTable}
											>
												Reopen
											</Button>
										</Col>
									)}
							</Row>
						</Col>
						<Col span={24}>
							<RenderTable />
						</Col>
					</Row>
				)}
			</Flex>
			{contextHolder}
			<ModalViewData />
			<ModalComment />
		</Layout>
	);
};

export default InventoryDashboard;
